import { FC } from 'react';
import { CardImage, IconAndText, Link } from '@blueprint/ui';
import {
    StyledCard,
    PropertyDetails,
    StyledPropertyTitle,
    StyledPropertyAddress,
    AgentImage,
    PropertyDetailsContainer,
    PropertyImage,
    StyledPropertyPrice,
    FeatureWrapper,
    FeaturePropertyCardWrapper,
    AgentImageWrapper,
    StyledPropertySubPrice
} from './FeaturedProperties.styles';
import icons from '@blueprint/ui/dist/Icon/icons';

export type Props = {
    property: {
        listingId: string;
        title?: string | null;
        price?: string | null;
        alternativeRentFrequencyLabel?: string | null;
        address?: string | null;
        featuredType?: string | null;
        branch?: {
            logoUrl?: string | null;
            name?: string | null;
            phone?: string | null;
        } | null;
        listingUris?: {
            detail?: string | null;
        } | null;
        features?:
            | ({
                  iconId?: string | null;
                  content?: number | null;
              } | null)[]
            | null;
        image?: {
            src?: string | null;
            responsiveImgList?:
                | ({ src?: string | null; width?: number | null } | null)[]
                | null;
        } | null;
    };
    onClick?: () => void;
    searchIdentifier: string;
};

const FeaturedPropertyCard: FC<Props> = (props) => {
    const { property, onClick, searchIdentifier } = props;

    const { featuredType } = property;

    const query = new URLSearchParams({
        [featuredType as string]: '1',
        utm_content: 'featured_listing'
    });

    if (searchIdentifier) {
        query.append('search_identifier', searchIdentifier);
    }

    const queryString = query.toString();

    const detailsLink = props.property.listingUris?.detail
        ? `${props.property.listingUris?.detail}?${queryString}`
        : '#';

    const availableFeatures =
        property.features?.filter(
            (feature) =>
                typeof feature?.content === 'number' && feature.content > 0
        ) ?? [];

    return (
        <FeaturePropertyCardWrapper>
            <StyledCard
                onClick={onClick}
                data-testid="featured-property-card"
                hasShadow={false}
                hasBorder={false}
                hasBackground
                contentAlignment="left"
            >
                <PropertyImage
                    plain
                    href={detailsLink}
                    data-testid="featured-property-card"
                    title="Property image"
                >
                    <CardImage
                        src={property.image?.src ?? ''}
                        responsiveImgList={
                            property.image?.responsiveImgList?.map((i) => ({
                                src: i?.src ?? '',
                                width: i?.width ?? 0
                            })) ?? []
                        }
                    />
                </PropertyImage>
                <PropertyDetailsContainer>
                    <PropertyDetails plain href={detailsLink}>
                        <StyledPropertyTitle>
                            {property.title}
                        </StyledPropertyTitle>

                        <StyledPropertyPrice>
                            {property.price}
                        </StyledPropertyPrice>
                        {property.alternativeRentFrequencyLabel && (
                            <StyledPropertySubPrice data-testid={'sub-price'}>
                                {property.alternativeRentFrequencyLabel}
                            </StyledPropertySubPrice>
                        )}

                        <StyledPropertyAddress>
                            {property.address}
                        </StyledPropertyAddress>
                        <FeatureWrapper>
                            {availableFeatures.map((item, index) => {
                                const { iconId, content } = item ?? {};

                                if (!iconId) {
                                    return null;
                                }

                                return (
                                    <IconAndText
                                        isInline
                                        key={`${index}-${iconId}`}
                                        iconId={iconId as keyof typeof icons}
                                        content={content ?? ''}
                                        iconColor={'grey-steel'}
                                        contentColor={'grey-slate'}
                                        size="medium"
                                    />
                                );
                            })}
                        </FeatureWrapper>
                    </PropertyDetails>
                    <Link plain href={detailsLink}>
                        <AgentImageWrapper>
                            <AgentImage
                                src={property.branch?.logoUrl ?? ''}
                                alt={property.branch?.name ?? ''}
                            />
                        </AgentImageWrapper>
                    </Link>
                </PropertyDetailsContainer>
            </StyledCard>
        </FeaturePropertyCardWrapper>
    );
};

export default FeaturedPropertyCard;
